import PlusIcon from '@/assets/Icons/components/data-table/PlusIcon';
import EditIcon from '@/assets/Icons/components/EditIcon';
import Button from '@/components/Button/Button';
import { useState, useEffect } from 'react';
import {
  Column,
  NormalizedValue,
  Row,
  SavedValue,
  TableData,
  Value,
} from './types/CustomInputsTypes';
import TrashIcon from '@/assets/Icons/components/TrashIcon';
import { useLazyGetTableCellDataQuery } from '@/services/plaintiffSheets/plaintiffSheetsEndpoint';
import Modal from '../Modal/Modal';
import DataTablePopupData from './DataTablePopupData';
import UpdateTableIcon from '@/assets/Icons/components/table-icon/UpdateTableIcon';
import Close from '@/assets/Icons/components/table-icon/Close';
import { formatPercentage } from '@/utils/stringHandlers';

import DeleteRowPopup from './DeleteRowPopup';
import DiscardChangesPopup from './DiscardChangesPopup';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { setHasUnsavedChanges } from '@/reducers/formSlice';
import { setDataTableUpdated } from '@/reducers/dataTableUpdatedSlice';
import UserCustomerTextArea from './UserCustomerTextArea';

import Tooltip from '@/components/Tooltip/Tooltip';
import { trackEvent } from '@/utils/track-event';
import { PostHogEvents } from '@/utils/PostHogEvents';
import { PostHogMessages } from '@/utils/PostHogMessages';
import { getAuthData } from '@/utils/auth';

import { useLocation } from 'react-router-dom';

const CustomDataTable = ({
  tableData,
  onTableDataChange,
  isLockedByMe,
  lockedOwner,
  isReviewForm,
  alwaysEditable = false, // New prop to make table always editable
  handleSaveProgress,
  collaborationPopup,
  isPdf = false,
  isLabelShow = true,
  isCancel = false,
  onCancel,
  isDeleteIconShow = false,
}: {
  tableData: TableData;
  onTableDataChange?: (data: any) => void;
  handleSaveProgress?: any;
  isLockedByMe?: boolean;
  lockedOwner?: string;
  isReviewForm?: boolean;
  isShow?: boolean;
  alwaysEditable?: boolean;
  collaborationPopup?: (any: boolean) => void;
  isPdf?: boolean;
  isLabelShow?: boolean;
  isCancel?: boolean;
  shouldHighlight?: boolean;
  onCancel?: any;
  isDeleteIconShow?: boolean;
}) => {
  const { districtId } = getAuthData();
  const districtData = useSelector((state: RootState) => state.districtData.district);
  const previewDistrictId = useSelector((state: RootState) => state.sidebarCollapse.districtId);
  const [isEditMode, setIsEditMode] = useState(alwaysEditable);
  const factSheetStatus = useSelector((state: RootState) => state.factSheetStatus.status);
  const shouldHighlight =
    (factSheetStatus === 'user_review_requested' ||
      factSheetStatus === 'review_and_edits_requested' ||
      factSheetStatus === 'user_edits_requested') &&
    tableData?.is_admin_review;

  const highlightClass = shouldHighlight ? 'ring-2 ring-[#EF8B21] bg-[#FCF2E6]' : '';

  const location = useLocation();
  // const { addToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const closeModal = () => setIsModalOpen(false);
  const hasUnsavedChanges = useSelector(
    (state: RootState) => state?.form?.hasUnsavedChanges || false
  );

  const dispatch = useDispatch();
  const isPreview = location.pathname.includes('preview');
  const [cellParams, setCellParams] = useState({
    year: '',
    schoolLevel: '',
    tabName: tableData?.tabName || '',
  });

  const [trigger, { data: tablePopupData, isLoading: isTableDataLoading, isFetching }] =
    useLazyGetTableCellDataQuery();

  const [dataTablePopupData, setDataTablePopupData] = useState<{ data?: any } | null>(null);
  const [hasSignificantChanges, setHasSignificantChanges] = useState(false);
  const [fieldValue, setFieldValue] = useState(tableData?.field?.value || '');
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [isDiscardChangesPopupOpen, setIsDiscardChangesPopupOpenVisible] = useState<boolean>(false);

  useEffect(() => {
    setDataTablePopupData(tablePopupData?.data ?? '');
  }, [tablePopupData, isTableDataLoading]);

  const [columns, setColumns] = useState<Column[]>(() => {
    if (!tableData?.columnHeaders) return [];
    return [...tableData.columnHeaders];
  });

  const [originalTableData, setOriginalTableData] = useState(tableData);

  const normalizeValues = (values: Value[]): NormalizedValue[] => {
    if (!values) return [];

    return values.map((item) => {
      if (item.col && typeof item.value === 'object' && item.value !== null) {
        return {
          col: item.col,
          colOrder: item.col.order,
          colId: item.col.column_id,
          rowOrder: item.value.row_order,
          value: item.value.value || '',
        };
      } else if (item.column_id) {
        return {
          col: item.col,
          column_order: item.column_order,
          column_id: item.column_id,
          row_order: item.row_order,
          row_id: item.row_id,
          value: typeof item.value === 'string' ? item.value : '',
        };
      }
      return {
        column_order: item.column_order,
        column_id: item.column_id,
        row_order: item.row_order,
        row_id: item.row_id,
        value: typeof item.value === 'string' ? item.value : '',
      };
    });
  };

  const [localRows, setLocalRows] = useState<Row[]>(() => {
    if (!tableData?.values) return [];

    const normalizedValues = normalizeValues(tableData.values);
    const uniqueRowOrders = new Set<number>();
    normalizedValues.forEach((item) => {
      if (item.row_order !== undefined) {
        uniqueRowOrders.add(item.row_order);
      }
    });

    if (tableData?.rowHeaders && tableData.rowHeaders.length > 0) {
      return [...tableData.rowHeaders];
    }

    const defaultRows: Row[] = Array.from(uniqueRowOrders)
      .sort((a, b) => a - b)
      .map((rowOrder) => ({
        row_id: `row-${rowOrder}`,
        order: rowOrder,
        label: `Row ${rowOrder}`,
      }));

    return defaultRows.length > 0 ? defaultRows : [{ row_id: 'row-1', order: 1, label: 'Row 1' }];
  });

  const [localValues, setLocalValues] = useState<NormalizedValue[]>(() => {
    if (!tableData?.values) return [];
    return normalizeValues(tableData.values);
  });

  useEffect(() => {
    if (isCancel) {
      // Reset to original state
      setLocalValues(normalizeValues(originalTableData.values || []));
      setLocalRows(() => {
        if (originalTableData?.rowHeaders && originalTableData.rowHeaders.length > 0) {
          return [...originalTableData.rowHeaders];
        }
        const normalizedValues = normalizeValues(originalTableData.values || []);
        const uniqueRowOrders = new Set<number>();
        normalizedValues.forEach((item) => {
          if (item.row_order !== undefined) {
            uniqueRowOrders.add(item.row_order);
          }
        });
        const rows = Array.from(uniqueRowOrders)
          .sort((a, b) => a - b)
          .map((rowOrder) => ({
            row_id: `row-${rowOrder}`,
            order: rowOrder,
            label: `Row ${rowOrder}`,
          }));
        return rows.length > 0 ? rows : [{ row_id: 'row-1', order: 1, label: 'Row 1' }];
      });
      setFieldValue(originalTableData?.field?.value || '');
      setRowToDelete(null);
      setIsDeletePopupOpen(false);
      setIsEditMode(false);
      dispatch(setHasUnsavedChanges(false));

      // Notify parent component
      if (onTableDataChange) {
        onTableDataChange(
          getTableDataForSave(localRows, normalizeValues(originalTableData.values || []))
        );
      }
      onCancel();
    }
  }, [isCancel]);

  const [valueMap, setValueMap] = useState<Record<number, Record<number, string>>>({});

  const checkForSignificantChanges = (values: NormalizedValue[], rows: Row[]) => {
    if (tableData?.tabName !== 'Tab_45' && tableData?.tabName !== 'Tab_46') return false;

    const yearOverYearRows = rows.filter((row) => row.label?.includes('Year-Over-Year Change'));
    if (yearOverYearRows.length === 0) return false;

    for (const row of yearOverYearRows) {
      const rowValues = values.filter(
        (value) => value.row?.order === row.order || value.row_order === row.order
      );

      for (const val of rowValues) {
        const numValue = parseFloat(val.value);
        if (isNaN(numValue)) continue;
        if (numValue > 20 || numValue < -20) {
          return true;
        }
      }
    }

    return false;
  };

  useEffect(() => {
    const newValueMap: Record<number, Record<number, string>> = {};

    localRows.forEach((row) => {
      newValueMap[row.order] = {};
      columns.forEach((col) => {
        newValueMap[row.order][col.order] = '';
      });
    });

    localValues.forEach((item) => {
      const rowOrder = item.row_order;
      const colOrder = item.column_order || item.col?.order;

      if (rowOrder !== undefined && colOrder !== undefined) {
        if (!newValueMap[rowOrder]) {
          newValueMap[rowOrder] = {};
        }
        newValueMap[rowOrder][colOrder] = item.value || '';
      }
    });

    setValueMap(newValueMap);

    if (tableData?.tabName === 'Tab_45' || tableData?.tabName === 'Tab_46') {
      setHasSignificantChanges(checkForSignificantChanges(localValues, localRows));
    }
  }, [localValues, localRows, columns, tableData?.tabName]);

  useEffect(() => {
    if (tableData) {
      setOriginalTableData(tableData);
      setFieldValue(tableData?.field?.value || '');

      if (tableData.columnHeaders && tableData.columnHeaders.length > 0) {
        setColumns([...tableData.columnHeaders]);
      }

      const normalizedValues = normalizeValues(tableData.values || []);
      const uniqueRowOrders = new Set<number>();
      normalizedValues.forEach((item) => {
        if (item.row_order !== undefined) {
          uniqueRowOrders.add(item.row_order);
        }
      });

      let newRows: Row[] = [];
      if (tableData?.rowHeaders && tableData.rowHeaders.length > 0) {
        newRows = [...tableData.rowHeaders];
      } else {
        newRows = Array.from(uniqueRowOrders)
          .sort((a, b) => a - b)
          .map((rowOrder) => ({
            row_id: `row-${rowOrder}`,
            order: rowOrder,
            label: `Row ${rowOrder}`,
          }));

        if (newRows.length === 0) {
          newRows = [{ row_id: 'row-1', order: 1, label: 'Row 1' }];
        }
      }

      if (tableData?.additional_config?.headers?.rowHeader) {
        const rowHeaderColumn = tableData.columnHeaders?.find((col) => col.order === 1);

        if (rowHeaderColumn) {
          newRows.forEach((row) => {
            const existingValue = normalizedValues.find(
              (v) =>
                (v.row?.order === row.order || v.row_order === row.order) &&
                (v.col?.order === 1 || v.column_order === 1)
            );

            if (!existingValue) {
              normalizedValues.push({
                col: rowHeaderColumn,
                row: row,
                column_order: 1,
                row_order: row.order,
                value: row.label,
              });
            } else if (existingValue.value !== row.label) {
              existingValue.value = row.label;
            }
          });
        }
      }

      setLocalRows(newRows);
      setLocalValues(normalizedValues);

      if (tableData?.tabName === 'Tab_45' || tableData?.tabName === 'Tab_46') {
        setHasSignificantChanges(checkForSignificantChanges(normalizedValues, newRows));
      }
    }
  }, [tableData]);

  useEffect(() => {
    if (alwaysEditable !== isEditMode) {
      setIsEditMode(alwaysEditable);
    }
  }, [alwaysEditable]);

  useEffect(() => {
    if (onTableDataChange && tableData?.field) {
      onTableDataChange({
        table_id: tableData.table_id,
        values: getTableDataForSave(localRows, localValues).values,
        nested_fields: {
          [tableData.field.field_id]: fieldValue,
        },
        isInitial: true,
      });
    }
  }, [tableData?.field?.field_id]);

  const getTableDataForSave = (rows: Row[], values: NormalizedValue[]) => {
    const valuesForSave: SavedValue[] = values.map((item) => {
      if (item.col && item.row) {
        return {
          row_id: item.row_id,
          row_order: item.row.order,
          column_id: item.col.column_id,
          column_order: item.col.order,
          value: item.value || '',
        };
      } else {
        return {
          row_order: item.row_order || 0,
          column_order: item.column_order || 0,
          column_id: item.column_id,
          value: item.value || '',
          row_id: item.row_id,
        };
      }
    });

    rows.forEach((row) => {
      columns.forEach((col) => {
        if (
          !values.some(
            (v) =>
              (v.row?.order === row.order || v.row_order === row.order) &&
              (v.col?.order === col.order || v.column_order === col.order)
          )
        ) {
          const isRowHeaderColumn =
            col.order === 1 && tableData?.additional_config?.headers?.rowHeader;
          const value = isRowHeaderColumn ? row.label : '';

          valuesForSave.push({
            row_order: row.order,
            column_order: col.order,
            column_id: col.column_id,
            value: value,
            row_id: additionalRows ? '' : row.row_id,
          });
        }
      });
    });

    return {
      table_id: tableData.table_id,
      values: valuesForSave,
      // Include the nested field data when saving
      nested_fields: tableData?.field
        ? {
            [tableData.field.field_id]: fieldValue,
          }
        : {},
    };
  };

  const handleFocus = () => {
    trackEvent(PostHogEvents.FORM_FIELD_FOCUSED, {
      field_name: tableData.field.label,
      form_id: tableData.field.field_id, // Make sure formId is in scope
      message: PostHogMessages[PostHogEvents.FORM_FIELD_FOCUSED],
    });
  };

  const handleCancel = () => {
    const normalizedValues = normalizeValues(originalTableData.values || []);
    let resetRows: Row[] = [];
    if (originalTableData?.rowHeaders && originalTableData.rowHeaders.length > 0) {
      resetRows = [...originalTableData.rowHeaders];
    } else {
      const uniqueRowOrders = new Set<number>();
      normalizedValues.forEach((item) => {
        if (item.row_order !== undefined) {
          uniqueRowOrders.add(item.row_order);
        }
      });

      resetRows = Array.from(uniqueRowOrders)
        .sort((a, b) => a - b)
        .map((rowOrder) => ({
          row_id: `row-${rowOrder}`,
          order: rowOrder,
          label: `Row ${rowOrder}`,
        }));

      if (resetRows.length === 0) {
        resetRows = [{ row_id: 'row-1', order: 1, label: 'Row 1' }];
      }
    }

    if (originalTableData.columnHeaders && originalTableData.columnHeaders.length > 0) {
      setColumns([...originalTableData.columnHeaders]);
    }

    setLocalRows(resetRows);
    setLocalValues(normalizedValues);
    setFieldValue(originalTableData?.field?.value || '');

    // Reset any pending delete operations
    setRowToDelete(null);
    setIsDeletePopupOpen(false);

    // Update the table data through callback
    if (onTableDataChange) {
      onTableDataChange(getTableDataForSave(resetRows, normalizedValues));
    }

    if (!alwaysEditable) {
      setIsEditMode(false);
    }
    setIsDiscardChangesPopupOpenVisible(false);
    dispatch(setHasUnsavedChanges(false));
  };

  const toggleEditMode = () => {
    if (isEditMode) {
      if (onTableDataChange) {
        console.log(`changeeeee`);
        onTableDataChange(getTableDataForSave(localRows, localValues));
      }
      dispatch(setHasUnsavedChanges(false));
      handleSaveProgress();
    }
    dispatch(setDataTableUpdated(true));
    if (!alwaysEditable) {
      setIsEditMode(!isEditMode);
    }
  };

  const handleAddRow = (afterIndex: number) => {
    if (!tableData?.additional_config?.additionalRows && !isEditMode && !alwaysEditable) return;

    const emptyCells: { rowIndex: number; columnIndex: number }[] = [];
    localRows.forEach((row, rowIndex) => {
      columns.forEach((column, colIndex) => {
        const cellValue = valueMap[row.order]?.[column.order];
        if (!cellValue || cellValue.trim() === '') {
          emptyCells.push({ rowIndex, columnIndex: colIndex });
        }
      });
    });

    if (emptyCells.length > 0) {
      emptyCells.forEach(({ rowIndex, columnIndex }) => {
        const cellElement = document.querySelector(
          `[data-row-index="${rowIndex}"][data-column-index="${columnIndex}"] input`
        );
        if (cellElement) {
          cellElement.classList.add('border-red-500');
          setTimeout(() => {
            cellElement.classList.remove('border-red-500');
          }, 3000);
        }
      });
      return;
    }

    const rowsArray = [...localRows];
    const insertAtIndex = afterIndex + 1;
    const afterRowOrder = localRows[afterIndex].order;
    const nextRowOrder = rowsArray[afterIndex + 1]?.order;

    const newOrder =
      nextRowOrder !== undefined ? (afterRowOrder + nextRowOrder) / 2 : afterRowOrder + 1;

    const newRowId = `row-${Date.now()}`;
    const newRow: Row = {
      row_id: newRowId,
      order: newOrder,
      label: `Row ${rowsArray.length + 1}`,
    };

    const updatedRows = [
      ...rowsArray.slice(0, insertAtIndex),
      newRow,
      ...rowsArray.slice(insertAtIndex),
    ];

    const updatedValues = [...localValues];
    columns.forEach((column) => {
      updatedValues.push({
        col: column,
        row: newRow,
        column_order: column.order,
        row_order: newRow.order,
        value: '',
      });
    });

    setLocalRows(updatedRows);
    setLocalValues(updatedValues);

    if (onTableDataChange) {
      onTableDataChange(getTableDataForSave(updatedRows, updatedValues));
    }
  };
  const handleDeleteClick = (rowId: string) => {
    setRowToDelete(rowId);
    setIsDeletePopupOpen(true);
  };

  const confirmDelete = () => {
    if (rowToDelete) {
      handleDeleteRow(rowToDelete);
    }
    setIsDeletePopupOpen(false);
    setRowToDelete(null);
  };

  const cancelDelete = () => {
    setIsDeletePopupOpen(false);
    setRowToDelete(null);
  };

  const handleDeleteRow = (rowId: string) => {
    // Don't allow deleting if it's the last row
    if (isReviewForm) return;
    if (localRows.length <= 1) return;

    const rowToDelete = localRows.find((row) => row.row_id === rowId);
    if (!rowToDelete) return;

    const updatedRows = localRows.filter((row) => row.row_id !== rowId);
    const updatedValues = localValues.filter((value) => {
      const rowMatches =
        value.row?.row_id === rowId ||
        value.row_id === rowId ||
        value.row?.order === rowToDelete.order ||
        value.row_order === rowToDelete.order;
      return !rowMatches;
    });

    const reorderedRows = updatedRows.map((row, index) => ({
      ...row,
      order: index + 1,
      label: `Row ${index + 1}`,
    }));

    const orderMapping: Record<number, number> = {};
    updatedRows.forEach((oldRow, index) => {
      orderMapping[oldRow.order] = reorderedRows[index].order;
    });

    const reorderedValues = updatedValues.map((value) => {
      const oldRowOrder = value.row?.order || value.row_order;
      const newRowOrder = oldRowOrder !== undefined ? orderMapping[oldRowOrder] : undefined;
      const associatedRow =
        newRowOrder !== undefined
          ? reorderedRows.find((row) => row.order === newRowOrder)
          : undefined;

      if (associatedRow && oldRowOrder !== undefined) {
        return {
          ...value,
          row: associatedRow,
          row_order: associatedRow.order,
        };
      }

      return value;
    });

    setLocalRows(reorderedRows);
    setLocalValues(reorderedValues);

    if (onTableDataChange) {
      onTableDataChange(getTableDataForSave(reorderedRows, reorderedValues));
    }
  };

  const handleValueChange = (row: Row, column: Column, value: string) => {
    if (!tableData?.additional_config?.additionalRows && !isEditMode && !alwaysEditable) return;

    const existingValueIndex = localValues.findIndex(
      (v) =>
        (v.row?.order === row.order || v.row_order === row.order) &&
        (v.col?.order === column.order || v.column_order === column.order)
    );

    let updatedValues;

    if (existingValueIndex >= 0) {
      updatedValues = [...localValues];
      updatedValues[existingValueIndex] = {
        ...updatedValues[existingValueIndex],
        value: value,
      };
    } else {
      updatedValues = [
        ...localValues,
        {
          col: column,
          row: row,
          column_order: column.order,
          row_order: row.order,
          value: value,
        },
      ];
    }

    setLocalValues(updatedValues);

    const newValueMap = { ...valueMap };
    if (!newValueMap[row.order]) {
      newValueMap[row.order] = {};
    }
    newValueMap[row.order][column.order] = value;
    setValueMap(newValueMap);

    const currentRowIndex = localRows.findIndex((r) => r.order === row.order);
    if (currentRowIndex >= 0 && currentRowIndex < localRows.length - 1) {
      const nextRow = localRows[currentRowIndex + 1];
      if (nextRow?.label?.includes('Year-Over-Year')) {
        calculateYearOverYearChange(row.order, updatedValues, newValueMap);
      }
    }

    if (alwaysEditable) {
      if (onTableDataChange) {
        onTableDataChange(getTableDataForSave(localRows, updatedValues));
      }
    } else {
      if (onTableDataChange) {
        onTableDataChange(getTableDataForSave(localRows, updatedValues));
      }
    }

    if (tableData?.tabName === 'Tab_45' || tableData?.tabName === 'Tab_46') {
      setHasSignificantChanges(checkForSignificantChanges(updatedValues, localRows));
    }
  };

  const calculateYearOverYearChange = (
    changedRowOrder: number,
    currentValues: NormalizedValue[],
    currentValueMap: Record<number, Record<number, string>>
  ) => {
    const yearOverYearRow = localRows.find((r) => r.order === changedRowOrder + 1);
    if (!yearOverYearRow || !yearOverYearRow?.label.includes('Year-Over-Year')) return;

    const dataRow = localRows.find((r) => r.order === changedRowOrder);
    if (!dataRow) return;

    const allColumns = [...columns].sort((a, b) => a.order - b.order);

    allColumns.forEach((col) => {
      if (showRowHeaders && col.order === 1) return;

      const currentValStr = currentValueMap[dataRow.order]?.[col.order] || '';
      if (currentValStr === '') return;

      const currentVal = parseFloat(currentValStr) || 0;
      const prevCol = allColumns[col.order - 2];
      if (!prevCol) return;

      const prevValStr = currentValueMap[dataRow.order]?.[prevCol.order] || '0';
      const prevVal = parseFloat(prevValStr) || 0;

      let percentageChange = 0;
      if (prevVal !== 0) {
        percentageChange = ((currentVal - prevVal) / Math.abs(prevVal)) * 100;
      } else if (currentVal !== 0) {
        percentageChange = NaN;
      }

      const formattedPercentage = isNaN(percentageChange)
        ? '-'
        : formatPercentage(percentageChange);

      const existingValueIndex = currentValues.findIndex(
        (v) =>
          (v.row?.order === yearOverYearRow.order || v.row_order === yearOverYearRow.order) &&
          (v.col?.order === col.order || v.column_order === col.order)
      );

      if (existingValueIndex >= 0) {
        currentValues[existingValueIndex].value = formattedPercentage;
      } else {
        currentValues.push({
          col: col,
          row: yearOverYearRow,
          column_order: col.order,
          row_order: yearOverYearRow.order,
          value: formattedPercentage,
        });
      }

      if (!currentValueMap[yearOverYearRow.order]) {
        currentValueMap[yearOverYearRow.order] = {};
      }
      currentValueMap[yearOverYearRow.order][col.order] = formattedPercentage;
    });

    setLocalValues([...currentValues]);
    setValueMap({ ...currentValueMap });

    if (tableData?.tabName === 'Tab_45' || tableData?.tabName === 'Tab_46') {
      setHasSignificantChanges(checkForSignificantChanges(currentValues, localRows));
    }
  };

  const handleCellClick = (row: Row, column: Column) => {
    if (
      isEditMode ||
      tableData?.additional_config?.additionalRows ||
      !tableData.tabName ||
      tableData.tabName == 'Tab_11' ||
      tableData.tabName == 'Tab_12' ||
      tableData.tabName == 'Tab_44' ||
      tableData.tabName == 'Tab_45' ||
      tableData.tabName == 'Tab_46'
    )
      return;

    const cellValue = valueMap[row.order]?.[column.order] || '';
    if (!cellValue) return;

    const year = column.label.split('-')[0];
    const rowHeaderColumn = columns.find((col) => col.order === 1);
    if (!rowHeaderColumn) return;

    const schoolLevel = valueMap[row.order]?.[1].toUpperCase() || row.label.toUpperCase();
    const tabName = tableData?.tabName || '';

    const isUser = location.pathname.includes('user');

    const district_Id = isUser
      ? districtId
      : isPreview
        ? previewDistrictId
        : districtData?.district_Id;

    const params = { year, schoolLevel, tabName, districtId: district_Id };
    trigger(params);
    setCellParams(cellParams);
    setIsModalOpen(true);
  };

  const handleTextAreaChange = (fieldId: string) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e?.target?.value || '';
    setFieldValue(newValue);

    if (onTableDataChange) {
      const dataToSave = getTableDataForSave(localRows, localValues);
      onTableDataChange({
        ...dataToSave,
        nested_fields: {
          [fieldId]: newValue,
        },
      });
    }
  };

  if (!tableData) {
    return <div className="p-4 text-center">No table data available</div>;
  }

  const isRowEditable = (row: Row) => {
    return !row.label?.includes('Year-Over-Year Change');
  };

  const { label, columnGroups, additional_config } = tableData;
  const { columnGroupingEnabled, additionalRows } = additional_config || {};
  const showColumnHeaders = additional_config?.headers?.columnHeader;
  const showRowHeaders = additional_config?.headers?.rowHeader;
  const rowHeaderColumn: Column | undefined = showRowHeaders
    ? columns.find((col) => col.order === 1)
    : undefined;
  const rowHeaderLabel = rowHeaderColumn?.label || 'Row';

  const dataColumns =
    showRowHeaders && columns.length > 0 ? columns.filter((col) => col.order !== 1) : columns;

  const openDiscardChangesPopup = () => setIsDiscardChangesPopupOpenVisible(true);
  const closeDiscardChangesPopup = () => {
    trackEvent(PostHogEvents.DISCARD_CHANGES_POPUP, {
      tittle: 'closeDiscardChangesPopup',
      timestamp: new Date().toISOString(),
    });
    setIsDiscardChangesPopupOpenVisible(false);
    dispatch(setHasUnsavedChanges(true));
  };

  const restrictedLabels = [
    '13. Provide the total number of on-campus health care workers (e.g., social workers, nurses, psychologists, psychiatrists, counselors, etc.) employed by the district from 2017-2018 to present or to the year for which data is most currently available (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
    '14. Provide the total number of off-campus health care workers (e.g., social workers, nurses, psychologists, psychiatrists, counselors, etc.) employed by the district from 2017-2018 to present or to the year for which data is most currently available (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
    '15. Other than health care workers identified above, does the district have other employees who provide mental health services? If yes, provide the total number of such employees from 2017-2018 to present or to the year for which data is most currently available and their positions (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
    '16. Provide the total number of health care workers (e.g., social workers, nurses, psychologists, psychiatrists, counselors) employed by the district whose primary responsibilities include addressing student mental health issues from 2017-2018 to present or to the year for which data is most currently available and their positions (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
    '9. Provide the number of students in your district referred for mental health services, if such referrals are tracked. Note: This question is not designed to require review of underlying individual student records.',
  ];

  return (
    <div>
      {label && isLabelShow && (
        <div className="flex items-center w-full justify-between py-3">
          <h2 className="text-[#242424] font-semibold mr-4 w-[95%] label-text">
            {additionalRows ? '' : label}
          </h2>
          {/* Only show Edit/Cancel/Update buttons if alwaysEditable is false */}
          {!additionalRows && !alwaysEditable && !isReviewForm && !isPdf && !isPreview && (
            <div className="flex items-center gap-4">
              {isEditMode && (
                <Button
                  size="xs"
                  color="outlined"
                  onClick={openDiscardChangesPopup}
                  disabled={!hasUnsavedChanges}
                >
                  <Close />
                </Button>
              )}
              <Button
                size={isEditMode ? `xs` : 'sm'}
                color={isEditMode ? 'primary' : 'outlined'}
                startIcon={isEditMode ? '' : <EditIcon />}
                onClick={toggleEditMode}
                disabled={lockedOwner !== '' && !isLockedByMe}
              >
                {isEditMode ? <UpdateTableIcon /> : 'Edit'}
              </Button>
            </div>
          )}
        </div>
      )}
      {isDiscardChangesPopupOpen && (
        <Modal
          isOpen={isDiscardChangesPopupOpen}
          onClose={closeDiscardChangesPopup}
          title="Discard changes?"
          size="lg"
          modalName="Discard chaanges model"
        >
          <DiscardChangesPopup onCancel={closeDiscardChangesPopup} onConfirm={handleCancel} />
        </Modal>
      )}

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={'School Data'}
          size="xxl"
          modalName="delete_row_modal"
        >
          <DataTablePopupData
            tableData={dataTablePopupData}
            isLoading={isTableDataLoading || isFetching}
          />
        </Modal>
      )}

      <div className={`bg-blue-50 overflow-hidden ${highlightClass}`}>
        <table className="w-full border-collapse">
          <thead>
            {showColumnHeaders && (
              <>
                {columnGroupingEnabled && columnGroups && columnGroups.length > 0 && (
                  <tr>
                    {showRowHeaders && (
                      <th className="translate-y-4 text-sm text-left sm:px-[0.5px] py-3 xl:px-2 font-semibold bg-blue-50 text-gray-550 border-ds-gray-200">
                        {rowHeaderLabel}
                      </th>
                    )}

                    {additionalRows && <th className="w-8"></th>}

                    <th
                      className="border-l border-b text-gray-550 text-center text-sm font-semibold border-ds-gray-200 p-2 bg-mc-blue-100"
                      colSpan={dataColumns.length}
                    >
                      {columnGroups[0]?.name || ''}
                    </th>

                    {additionalRows && <th className="w-10"></th>}
                  </tr>
                )}

                <tr>
                  {/* Cell for add row buttons if additionalRows is enabled */}
                  {additionalRows && !isReviewForm && <th className="w-8"></th>}

                  {showRowHeaders && (
                    <th className="text-sm text-left p-2 font-semibold bg-mc-blue-100 text-gray-550 border-ds-gray-200"></th>
                  )}

                  {dataColumns.map((column) => (
                    <th
                      key={`col-${column.column_id}`}
                      className="border-l py-2 sm:px-[0.5px] xl:px-1 text-sm text-gray-550 font-semibold bg-mc-blue-100 text-center border-ds-gray-200"
                    >
                      {column.label}
                    </th>
                  ))}

                  {/* Add an extra header column for the trash icon */}
                  {additionalRows && !isReviewForm && (
                    <th className="w-10 border-l py-2 px-2 text-sm text-gray-550 font-semibold bg-mc-blue-100 text-center border-ds-gray-200">
                      Action
                    </th>
                  )}
                </tr>
              </>
            )}
          </thead>

          <tbody>
            {localRows.map((row, index) => (
              <tr key={`row-${row.row_id}`} className="bg-white border border-ds-gray-100">
                {/* Add row button cell */}
                {additionalRows && !isReviewForm && (
                  <td className="w-14 border text-center">
                    <Tooltip
                      content={' Please fill all fields before adding a new row'}
                      position="bottom"
                      width="150px"
                    >
                      <button
                        onClick={() => handleAddRow(index)}
                        className={`w-14 h-6 flex items-center justify-center rounded-full focus:outline-none ${isReviewForm ? 'cursor-default' : 'cursor-pointer'}`}
                        aria-label="Add row"
                      >
                        <PlusIcon />
                      </button>
                    </Tooltip>
                  </td>
                )}

                {showRowHeaders && (
                  <td className="sm:p-[0.5px] xl:p-2 text-gray-550 text-xs">{row.label}</td>
                )}

                {dataColumns.map((column) => {
                  const cellValues = valueMap[row.order]?.[column.order] || '';
                  const isYearOverYearRow = row.label?.includes('Year-Over-Year');
                  const isPosition = row.label?.includes('Position');
                  const isYear = column.label === 'Year';

                  let cellValue;
                  if (isYearOverYearRow) {
                    if (cellValues.trim().endsWith('%')) {
                      cellValue = cellValues;
                    } else {
                      const numValue = parseFloat(cellValues);
                      cellValue = isNaN(numValue) ? cellValues : `${numValue}%`;
                    }
                  } else {
                    const isNumeric = /^[0-9]+(\.[0-9]+)?$/.test(cellValues.trim());
                    cellValue = isNumeric ? Number(cellValues) : cellValues;
                  }

                  return (
                    <td
                      key={`cell-${row.row_id}-${column.column_id}`}
                      className="sm:px-[0.5px] py-3  xl:px-1 text-center border text-ds-gray-500 text-xs border-ds-gray-100 text-wrap"
                    >
                      {(isEditMode || additionalRows || alwaysEditable) && !isPdf ? (
                        <input
                          type={
                            isPosition
                              ? 'text'
                              : column.data_type === 'string'
                                ? 'text'
                                : column.data_type === 'number'
                                  ? 'text'
                                  : column.data_type
                          }
                          inputMode={
                            column.data_type === 'number' && !isPosition && !isYear
                              ? 'numeric'
                              : undefined
                          }
                          className="w-full bg-transparent rounded focus:outline-none focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-wrap"
                          value={cellValue}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent parent click events
                            // Show collaboration popup if field is locked by another user
                            if (lockedOwner !== '' && !isLockedByMe) {
                              if (collaborationPopup) {
                                collaborationPopup(true);
                              }
                            }
                          }}
                          placeholder={
                            isPosition
                              ? 'Enter text '
                              : isYear
                                ? 'Enter year'
                                : isYearOverYearRow
                                  ? `${cellValue || '-'}`
                                  : column.data_type === 'number'
                                    ? 'Enter value'
                                    : [
                                          '13. Provide the total number of on-campus health care workers (e.g., social workers, nurses, psychologists, psychiatrists, counselors, etc.) employed by the district from 2017-2018 to present or to the year for which data is most currently available (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
                                          '14. Provide the total number of off-campus health care workers (e.g., social workers, nurses, psychologists, psychiatrists, counselors, etc.) employed by the district from 2017-2018 to present or to the year for which data is most currently available (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
                                          '15. Other than health care workers identified above, does the district have other employees who provide mental health services? If yes, provide the total number of such employees from 2017-2018 to present or to the year for which data is most currently available and their positions (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
                                          '16. Provide the total number of health care workers (e.g., social workers, nurses, psychologists, psychiatrists, counselors) employed by the district whose primary responsibilities include addressing student mental health issues from 2017-2018 to present or to the year for which data is most currently available and their positions (specified as FTE if available), as well as the total number of volunteers in these positions, if any:',
                                          '9. Provide the number of students in your district referred for mental health services, if such referrals are tracked. Note: This question is not designed to require review of underlying individual student records.',
                                        ].includes(tableData.label)
                                      ? 'Enter value'
                                      : 'Enter text'
                          }
                          // In your input element's onChange handler:
                          onChange={(e) => {
                            let value = e.target.value;

                            // For number fields (not position or year fields)
                            if (column.data_type === 'number' && !isPosition && !isYear) {
                              // Prevent scientific notation (e/E) and allow only numbers and decimal point
                              value = value.replace(/[^0-9.]/g, '');

                              // Ensure only one decimal point
                              const parts = value.split('.');
                              if (parts.length > 2) {
                                value = parts[0] + '.' + parts.slice(1).join('');
                              }

                              // Limit to 11 digits before decimal
                              const [wholePart] = value.split('.');
                              if (wholePart && wholePart.length > 11) {
                                return;
                              }

                              handleValueChange(row, column, value);
                            }
                            // For position fields
                            else if (isPosition || column.data_type === 'string') {
                              // Allow letters and spaces only
                              const maxLength = restrictedLabels.includes(tableData.label)
                                ? 15
                                : 25;

                              if (value.length > maxLength) return;

                              handleValueChange(row, column, value);
                            }

                            // For year fields
                            else if (isYear) {
                              // Allow numbers and dash only
                              value = value.replace(/[^0-9-]/g, '');
                              handleValueChange(row, column, value);
                            }
                            // For string fields
                            else {
                              handleValueChange(row, column, value);
                            }
                          }}
                          onKeyDown={(e) => {
                            // For number fields, prevent 'e', 'E', '+', '-' (except for year fields)
                            if (column.data_type === 'number' && !isYear && !isPosition) {
                              if (
                                e.key === 'e' ||
                                e.key === 'E' ||
                                e.key === '+' ||
                                e.key === '-'
                              ) {
                                e.preventDefault();
                              }
                            }

                            // Existing position field handling
                            if (isPosition) {
                              const allowedKeys = [
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                                ' ',
                              ];
                              const isLetter = !/[0-9]/.test(e.key);
                              const isSpace = e.key === ' ';
                              const isAllowedKey = allowedKeys.includes(e.key);
                              if (!isLetter && !isSpace && !isAllowedKey) {
                                e.preventDefault();
                              }
                            }
                          }}
                          readOnly={
                            (lockedOwner !== '' && !isLockedByMe) ||
                            isReviewForm ||
                            !isRowEditable(row)
                          }
                          disabled={false}
                        />
                      ) : (
                        <div
                          className={`w-full sm:p-[0.5px]  cursor-pointer ${tableData?.tabName && !['Tab_11', 'Tab_12', 'Tab_44', 'Tab_45', 'Tab_46'].includes(tableData.tabName) ? 'text-[#192971]  hover:underline' : ''} `}
                          onClick={() => handleCellClick(row, column)}
                        >
                          {typeof cellValue === 'number'
                            ? (cellValue as number).toLocaleString('en-US', {
                                maximumFractionDigits: 20,
                              })
                            : cellValue || '-'}
                        </div>
                      )}
                    </td>
                  );
                })}

                {/* Add trash icon in a separate column */}
                {((additionalRows && !isReviewForm) || (isDeleteIconShow && !isReviewForm)) && (
                  <td className="w-10 border text-center">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (localRows.length <= 1) {
                          // You might want to show a toast notification here
                          return;
                        }
                        if (!isLockedByMe && collaborationPopup && lockedOwner !== '') {
                          // Show collaboration popup
                          collaborationPopup(true);
                          return;
                        }
                        // All conditions met, proceed with delete
                        handleDeleteClick(row.row_id);
                      }}
                      className="w-8 h-6 flex items-center justify-center mx-auto focus:outline-none text-gray-500 hover:text-red-500"
                      aria-label="Delete row"
                    >
                      <TrashIcon />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isDeletePopupOpen && (
        <Modal
          isOpen={isDeletePopupOpen}
          onClose={cancelDelete}
          title="Delete Row"
          size="md"
          modalName="delete_row_popup_model"
        >
          <DeleteRowPopup onConfirm={confirmDelete} onCancel={cancelDelete} />
        </Modal>
      )}

      {hasSignificantChanges &&
        (tableData?.tabName === 'Tab_45' || tableData?.tabName === 'Tab_46') &&
        tableData?.field && (
          <UserCustomerTextArea
            label={''}
            name={tableData?.field.field_id}
            value={fieldValue}
            onChange={handleTextAreaChange(tableData?.field?.field_id)}
            placeholder={''}
            description={tableData?.field?.label}
            rows={8}
            isCondition={false}
            isLockedByMe={isLockedByMe}
            lockedOwner={lockedOwner}
            isReviewForm={isReviewForm}
            onFocus={handleFocus}
            isPdf={isPdf}
          />
        )}
    </div>
  );
};

export default CustomDataTable;
